import csiAction from "csi/action";
import JsonResponse from "csi/comm/JsonResponse";
import { ControllerSaveFunction } from "csi/middleware/common";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { StepInfo } from "./WizardDialog";

export default class WizardStore extends ReactionStore {
	@observable private _index: number = 0;
	@observable private _stepInfo: Record<number, StepInfo> = {};

	/** handler for closing the wizard */
	private readonly _onClose: () => void;

	/** handler for finishing the wizard */
	private readonly _onSave: ControllerSaveFunction;

	/** extension point called to pass page action message */
	protected readonly _actionMessage: () => string;

	/** extension point called  check if the primary button for the current page should be disabled */
	protected readonly _disablePrimaryButton: () => boolean;

	/** extension point called  check if the finish button for the current page should be disabled */
	protected readonly _disableFinishButton: () => boolean;

	/** extension point called  check if the finish button for the current page should be displayed */
	protected readonly _showFinishButton: () => boolean;

	/** locale provider */
	protected readonly locale: ILocaleProvider;

	/** handler for closing the wizard */
	protected readonly onCancel = (): boolean => true;

	/** extension point called during navigation to the next page */
	protected readonly onNextPage = async (index: number): Promise<boolean> => true;

	/** extension point called during navigation to the previous page */
	protected readonly onPreviousPage = async (index: number): Promise<boolean> => true;

	/** extension point called when wizard is finished */
	protected readonly onSavePromise = async (): Promise<JsonResponse> => null;

	/** extension point called when validation */
	protected readonly onValidate = async (): Promise<boolean> => true;

	/** action Id / resource Id */
	readonly actionId: string;

	/** page size of wizard */
	@observable readonly pageSize: number;

	/** page action message */
	get actionMessage(): string {
		return this._actionMessage?.() || "";
	}

	/** check if the Primary button for the current page should be disabled */
	get disablePrimaryButton(): boolean {
		return this._disablePrimaryButton?.() || false;
	}

	/** check if the Finish button for the current page should be disabled */
	get disableFinishButton(): boolean {
		return this._disableFinishButton?.() || false;
	}

	/** check if the Finish button for the current page should be displayed */
	get showFinishButton(): boolean {
		return this._showFinishButton?.() || this.index === this.pageSize - 1;
	}

	/** page index */
	@computed get index(): number {
		return this._index;
	}

	/** the current step information instance */
	@computed get currentStepInfo(): StepInfo {
		return this._stepInfo[this.index];
	}

	/** the whole step information instance map */
	@computed get stepInfoList(): Record<number, StepInfo> {
		return this._stepInfo;
	}

	/** close the dialog */
	@action cancel = (): void => {
		if (this.onCancel()) {
			this._onClose();
		}
	};

	/** finish the wizard */
	@action finish = async (): Promise<void> => {
		const promise = this.onSavePromise();
		await this._onSave(promise);
	};

	/** validate on save */
	@action validateOnSave = async (): Promise<boolean> => {
		return this.onValidate();
	};

	/** navigate to the next page */
	@action nextPage = async (): Promise<void> => {
		const valid = await this.onNextPage(this._index);
		if (valid) {
			runInAction(() => {
				this._index++;
			});
		}
	};

	/** navigate to the previous page */
	@action previousPage = async (target?: number): Promise<void> => {
		const valid = await this.onPreviousPage(this._index);
		if (valid) {
			runInAction(() => {
				if (target != undefined) {
					this._index = target;
				} else {
					this._index--;
				}

				// clear the values of current step 
				this.stepInfoList[this._index].values.length = 0;
			});
		}
	};

	/**
	 * @param pageSize total page of the wizard
	 * @param stepResourceId the resource id by which we load the step information
	 */
	constructor(pageSize: number, stepResourceId: string | undefined, onClose: () => void, onSave: ControllerSaveFunction, locale?: ILocaleProvider) {
		super();
		makeObservable(this);
		this.actionId = stepResourceId;
		this.pageSize = pageSize;
		this._onClose = onClose;
		this._onSave = onSave;
		this.locale = locale;

		// look up the resource, return blank if resource does not exist
		const _getResource = (suffix: string, index: number, defaultValue = ""): string => {
			const resourceId = `${stepResourceId}${suffix}${index}`;
			const result = this.locale.resource(resourceId);
			return (result == resourceId) ? defaultValue : result;
		};

		// populate the static step information
		if (stepResourceId) {
			for (let i = 0; i < pageSize; i++) {
				const title = `${i + 1}. ` + _getResource(csiAction.SUFFIX_TITLE, i, `Please config your step information for the page with the index = ${i}`);
				const subtitle = _getResource(csiAction.SUFFIX_SUBTITLE, i);
				const description = _getResource(csiAction.SUFFIX_DETAIL, i);

				this._stepInfo[i] = new StepInfo(title, subtitle, description);
			}
		}
	}
}